import React from 'react';
import { Typography } from '@mui/material';
import { EventOutlined, HighlightOff } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { Appointment, AppointmentRecurring, PatientAppointmentActivity, SessionFormField, SessionNotes } from '@uplift/api';
import { LightChip, Button, useBreakpoint } from '@uplift/ui-v2';
import { BaseActivityItem, BaseActivityItemProps } from '../BaseActivityItem';
import { Link } from 'react-router-dom';
import { providerSubmitSessionRoute } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useAppointmentCancellationDetailsQuery, useCancelAppointmentReasonsQuery } from '@uplift/queries';


export type TherapyAppointmentActivityItemProps = {
  appointment: Pick<Appointment, 'id' | 'type' | 'recurring'>;
  notes?: SessionNotes;
  onClick?: () => void;
  provider: {
    id: string;
    firstName: string;
    lastName: string;
  };
};


const formFieldsToView = (field: SessionFormField) => {
  if (typeof field.value === 'string') {
    return {
      label: field.label,
      value: field.value,
    };
  }

  if (Array.isArray(field.value)) {
    return {
      label: field.label,
      value: field.value.length ? field.value.join('. ') : null,
    };
  }

  return {
    label: field.label,
    value: null,
  };
};


export const TherapySessionBaseActivityItem: React.FC<TherapyAppointmentActivityItemProps & Pick<BaseActivityItemProps, 'onClick' | 'rightContent' | 'chip' | 'highlighted'>> = ({ appointment, notes: _notes, provider, ...props }) => {
  const l10n = useL10n();
  const title = l10n.appointments.type[appointment.type];
  const chip = React.useMemo(() => {
    if (appointment.recurring && appointment.recurring !== AppointmentRecurring.none) {
      return <LightChip label={l10n.appointments.recurring[appointment.recurring]} size="small" color="secondary" />;
    }

    return null;
  }, [ l10n, appointment.recurring ]);

  return (
    <BaseActivityItem
      title={title}
      color="secondary"
      icon={EventOutlined}
      chip={props.chip ?? chip}
      highlighted={props.highlighted}
      rightContent={props.rightContent ?? <Typography variant="body2" color="textPrimary">with {provider.firstName[0]}. {provider.lastName}</Typography>}
      children={props.children}
      onClick={props.onClick}
    />
  );
};


export const TherapySessionCompletedActivityItem: React.FC<TherapyAppointmentActivityItemProps> = ({ appointment, ...props }) => {
  const me = useCurrentuser();
  const isCurrentUserSession = me.id === props.provider.id;
  const breakpoint = useBreakpoint();
  const chip = React.useMemo(() => {
    if (!isCurrentUserSession) {
      return <LightChip label="Notes unavailable" size="small" />;
    }
    return null;
  }, [ isCurrentUserSession ]);

  return (
    <TherapySessionBaseActivityItem
      appointment={appointment}
      notes={props.notes}
      provider={props.provider}
      chip={chip}
      highlighted={isCurrentUserSession}
      rightContent={isCurrentUserSession ? (
        <Button
          component={Link}
          to={providerSubmitSessionRoute.createLink({ appointmentId: appointment.id })}
          variant="contained"
          color="secondary"
          size="small"
        >
          {breakpoint.md ? 'Add Progress Note' : 'Submit'}
        </Button>
      ) : undefined}
    />
  );
};


export const TherapySessionSubmittedActivityItem: React.FC<TherapyAppointmentActivityItemProps> = ({ appointment, ...props }) => {
  const l10n = useL10n();

  const sessionGoals = React.useMemo(() => {
    if (props.notes) {
      const notes = [
        props.notes.followUpProgress.formFields.map(formFieldsToView).filter(f => f.value),
        props.notes.initialProgress.formFields.map(formFieldsToView).filter(f => f.value),
      ].flat();

      if (notes.length) {
        return notes;
      }
    }

    return null;
  }, [ props.notes ]);

  const chip = React.useMemo(() => {
    if (appointment.recurring && appointment.recurring !== AppointmentRecurring.none) {
      return <LightChip label={l10n.appointments.recurring[appointment.recurring]} size="small" color="secondary" />;
    }

    return null;
  }, [ l10n, appointment.recurring ]);

  return (
    <BaseActivityItem
      title={l10n.appointments.type.therapy}
      color="secondary"
      icon={EventOutlined}
      chip={chip}
      rightContent={<Typography variant="body2" color="textPrimary">with {props.provider.firstName[0]}. {props.provider.lastName}</Typography>}
      onClick={props.onClick}
    >
      {sessionGoals?.map(sg => (
        <Typography key={sg.label + sg.value} color="textSecondary">
          <strong style={{ fontWeight: 500 }}>{sg.label}</strong>: {sg.value}
        </Typography>
      )) ?? null}
    </BaseActivityItem>
  );
};


export const SessionCancelledActivityItem: React.FC<{
  activity: PatientAppointmentActivity;
  onClick?: () => void;
}> = (props) => {
  const { data = [] } = useCancelAppointmentReasonsQuery({});
  const reason = data.find(r => r.id === props.activity.appointment.cancelReason);
  const { data: cancellationDetails } = useAppointmentCancellationDetailsQuery(props.activity.appointment.id);

  return (
    <BaseActivityItem
      onClick={props.onClick}
      title={(
        <Typography>{cancellationDetails ? {
          [AppointmentRecurring.none]: 'Appointment Canceled',
          [AppointmentRecurring.weekly]: 'Weekly Appointments Canceled',
          [AppointmentRecurring.biweekly]: 'Bi-Weekly Appointments Canceled',
          [AppointmentRecurring.monthly]: 'Monthly Appointments Canceled',
        }[cancellationDetails.cancellationType] : 'Appointment Canceled'}</Typography>
      )}
      icon={HighlightOff}
      color="warning"
      rightContent={(
        <Typography variant="body2" color="textPrimary" data-testid="referral-from-student-school">
          with {props.activity.provider.firstName}
        </Typography>
      )}
      testId="cancelled-appointment-activity-item"
    >
      {reason && (
        <Typography>
          <strong>Reason:</strong> {reason?.descriptionPastTense ?? reason?.description}
        </Typography>
      )}
    </BaseActivityItem>
  );
};
